import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { BillingLedger } from '../../../contexts/BillingContext';
import type { PricingSettings } from '../../../shared/constants/pricing';

interface SeatLicensesAndBillProps {
  ledger: BillingLedger | null;
  pricingSettings: PricingSettings;
  cycleEndDate: Date | null;
  activeUserCount: number | null;
  totalSeatCost: number;
  totalUnbilledUsageCost: number;
  totalEstimatedBill: number;
}

export const SeatLicensesAndBill: React.FC<SeatLicensesAndBillProps> = ({
  ledger,
  pricingSettings,
  cycleEndDate,
  activeUserCount,
  totalSeatCost,
  totalUnbilledUsageCost,
  totalEstimatedBill,
}) => {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 3, backgroundColor: theme.palette.greyLight, height: '100%' }}>
      <Typography variant="h5" gutterBottom>
        Seat Licenses & Current Bill
      </Typography>

      {cycleEndDate && (
        <Typography variant="body2" color="text.secondary" paragraph>
          Current billing cycle ends on: <strong>{cycleEndDate.toLocaleDateString()}</strong>
        </Typography>
      )}

      <Box sx={{ my: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Active Seats
        </Typography>
        <Typography variant="h4" color="primary.main">
          {activeUserCount !== null ? activeUserCount : '...'} Seats
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {activeUserCount !== null
            ? `${activeUserCount} × $${pricingSettings.seat_license_cost_aud.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ledger?.discount ? ` × ${1 - ledger.discount / 100} (discount)` : ''} = $${totalSeatCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AUD / month`
            : 'Calculating...'}
        </Typography>
      </Box>

      <Box sx={{ my: 3, p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Total Estimated Bill This Cycle
        </Typography>
        <Typography variant="h3" color="error.main">
          $
          {totalEstimatedBill.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          AUD
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Seats ($
          {totalSeatCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          ) + Usage ($
          {totalUnbilledUsageCost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          )
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary">
        To manage seats, navigate to the Team Settings and add or remove users.
      </Typography>
    </Paper>
  );
};
